/**
 * Splits a node name into up to 2 lines that fit within the given cell width.
 *
 * @param name - The name to split into lines.
 * @param width - The width of the cell in pixels.
 * @returns An array of 1-2 display lines.
 */
export const getTreeMapLabelLines = (name: string, width: number): string[] => {
  const maxCharsPerLine = Math.max(7, Math.floor((width - 16) / 6.4));
  const tokens = name.split(/[-_\s]+/).filter(Boolean);

  if (tokens.length === 0) {
    return name.length > maxCharsPerLine
      ? [`${name.slice(0, Math.max(4, maxCharsPerLine - 1))}…`]
      : [name];
  }

  const lines: string[] = [];
  let currentLine = '';

  for (const token of tokens) {
    const nextLine = currentLine ? `${currentLine}-${token}` : token;

    if (nextLine.length <= maxCharsPerLine) {
      currentLine = nextLine;
      continue;
    }

    if (!currentLine) {
      lines.push(`${token.slice(0, Math.max(4, maxCharsPerLine - 1))}…`);
    } else {
      lines.push(currentLine);
      currentLine =
        token.length > maxCharsPerLine
          ? `${token.slice(0, Math.max(4, maxCharsPerLine - 1))}…`
          : token;
    }

    if (lines.length === 2) {
      const lastLine = lines[1];
      if (!lastLine.endsWith('…')) {
        lines[1] = `${lastLine.slice(0, Math.max(4, maxCharsPerLine - 1))}…`;
      }
      return lines;
    }
  }

  if (currentLine && lines.length < 2) {
    lines.push(currentLine);
  }

  return lines;
};

export type TreeMapTooltipAnchor = {
  x: number;
  y: number;
};

export type TreeMapTooltipSize = {
  width: number;
  height: number;
};

export type TreeMapTooltipViewport = {
  width: number;
  height: number;
  margin?: number;
  gap?: number;
};

/**
 * Positions a cursor-following tooltip inside the viewport.
 * Prefers above-center; flips below / to the side near edges.
 */
export const getTreeMapTooltipPosition = (
  anchor: TreeMapTooltipAnchor,
  size: TreeMapTooltipSize,
  viewport: TreeMapTooltipViewport
): { left: number; top: number } => {
  const margin = viewport.margin ?? 8;
  const gap = viewport.gap ?? 12;
  const { width: tooltipW, height: tooltipH } = size;
  const { width: vw, height: vh } = viewport;
  const { x, y } = anchor;

  const fitsX = (left: number): boolean =>
    left >= margin && left + tooltipW <= vw - margin;

  let left = x - tooltipW / 2;
  let top = y - tooltipH - gap;

  if (!fitsX(left)) {
    if (x > vw / 2) {
      left = x - tooltipW - gap;
      if (!fitsX(left)) {
        left = Math.max(margin, vw - tooltipW - margin);
      }
    } else {
      left = x + gap;
      if (!fitsX(left)) {
        left = margin;
      }
    }
  }

  if (top < margin) {
    top = y + gap;
  }

  top = Math.max(margin, Math.min(top, vh - tooltipH - margin));

  return { left, top };
};
